import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Headset } from 'lucide-react';
import Modal from '../../components/common/Modal';
import { vrAccessAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import { formatDate } from '../../utils/dateFormatter';
import './VRAccess.css';

const blankForm = {
  name: '',
  description: '',
  meta_app_id: '',
  meta_app_secret: '',
  meta_channel_id: '',
  status: 'Active',
};

const VRChannels = () => {
  const { showNotification } = useAppContext();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vrAccessAPI.listChannels();
      setChannels(res.data?.data || []);
    } catch (err) {
      showNotification('Failed to load VR channels', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(blankForm);
    setModalOpen(true);
  };

  const openEdit = (channel) => {
    setEditing(channel);
    setForm({
      name: channel.name,
      description: channel.description || '',
      meta_app_id: channel.metaAppId,
      meta_app_secret: '',
      meta_channel_id: channel.metaChannelId,
      status: channel.status,
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.meta_app_secret) delete payload.meta_app_secret; // keep existing if blank
        await vrAccessAPI.updateChannel(editing.channelId.replace(/-/g, ''), payload);
        showNotification('VR channel updated', 'success');
      } else {
        await vrAccessAPI.createChannel(form);
        showNotification('VR channel created', 'success');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to save channel', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (channel) => {
    if (!window.confirm(`Delete channel "${channel.name}"? This will remove all VR users under it.`)) return;
    try {
      await vrAccessAPI.deleteChannel(channel.channelId.replace(/-/g, ''));
      showNotification('VR channel deleted', 'success');
      load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete channel', 'error');
    }
  };

  return (
    <div className="vr-access-page">
      <header className="vr-access-header">
        <div>
          <Link to="/vr-access" className="vr-back-link"><ArrowLeft size={14} /> Back to VR Access</Link>
          <h1><Headset size={22} /> VR Channels</h1>
          <p>Each channel maps to a VR experience and its Meta Developer Hub release channel.</p>
        </div>
        <div className="vr-access-header__actions">
          <button className="btn btn--primary" onClick={openNew}>
            <Plus size={16} /> Add Channel
          </button>
        </div>
      </header>

      <div className="vr-access-table-wrap">
        <table className="vr-access-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Meta App ID</th>
              <th>Meta Channel ID</th>
              <th>Users</th>
              <th>Status</th>
              <th>Created</th>
              <th className="vr-access-table__actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="vr-access-table__empty">Loading...</td></tr>
            )}
            {!loading && channels.length === 0 && (
              <tr><td colSpan="7" className="vr-access-table__empty">No channels yet. Add one to start inviting users.</td></tr>
            )}
            {!loading && channels.map(c => (
              <tr key={c.channelId}>
                <td>
                  <div className="vr-cell-strong">{c.name}</div>
                  {c.description && <div className="vr-cell-sub">{c.description}</div>}
                </td>
                <td className="vr-mono">{c.metaAppId}</td>
                <td className="vr-mono">{c.metaChannelId}</td>
                <td>{c.userCount ?? 0}</td>
                <td>
                  <span className={`vr-badge ${c.status === 'Active' ? 'vr-badge--accepted' : 'vr-badge--revoked'}`}>
                    {c.status}
                  </span>
                </td>
                <td>{c.createdAt ? formatDate(c.createdAt) : '—'}</td>
                <td className="vr-access-table__actions">
                  <button className="icon-btn" title="Edit" onClick={() => openEdit(c)}>
                    <Edit size={15} />
                  </button>
                  <button className="icon-btn icon-btn--danger" title="Delete" onClick={() => handleDelete(c)}>
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit VR Channel' : 'Add VR Channel'}
        size="md"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => setModalOpen(false)} disabled={submitting}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={submit} disabled={submitting}>
              {submitting ? 'Saving...' : (editing ? 'Save' : 'Create')}
            </button>
          </>
        }
      >
        <form className="vr-form" onSubmit={submit}>
          <label className="vr-field">
            <span>Name *</span>
            <input
              type="text" required
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Marine Safety VR"
            />
          </label>
          <label className="vr-field">
            <span>Description</span>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </label>
          <label className="vr-field">
            <span>Meta App ID *</span>
            <input
              type="text" required
              value={form.meta_app_id}
              onChange={(e) => setForm(f => ({ ...f, meta_app_id: e.target.value }))}
              placeholder="Numeric Meta app id"
            />
          </label>
          <label className="vr-field">
            <span>Meta App Secret {editing ? <span className="vr-muted">(leave blank to keep)</span> : '*'}</span>
            <input
              type="password"
              required={!editing}
              value={form.meta_app_secret}
              onChange={(e) => setForm(f => ({ ...f, meta_app_secret: e.target.value }))}
              placeholder="App access token secret"
              autoComplete="new-password"
            />
          </label>
          <label className="vr-field">
            <span>Meta Release Channel ID *</span>
            <input
              type="text" required
              value={form.meta_channel_id}
              onChange={(e) => setForm(f => ({ ...f, meta_channel_id: e.target.value }))}
              placeholder="Channel id from Meta Developer Hub"
            />
          </label>
          <label className="vr-field">
            <span>Status</span>
            <select
              value={form.status}
              onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </label>
        </form>
      </Modal>
    </div>
  );
};

export default VRChannels;
